import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation, useBusy, useToast } from "@repo/core";
import { usePeriod } from "./indicators/usePeriod";
import {
  useRankingRule,
  type RankingRuleType,
} from "./indicators/useRankingRule";
import { useIndicatorKey } from "@repo/api";
import {
  useRecalculateStrategy,
  useUpdateStrategy,
  useCloneStrategy,
  type Strategy,
  type PortfolioConstructionType,
  type RecalculateStrategy,
} from "@repo/api";
import type { StrategyDetailType } from "./useStrategyDetailType";
import type { Period } from "@repo/core";
import { useQueryClient } from "../context/QueryClientContext";

interface UseStrategyRecalculateHandlerOptions {
  strategy: Strategy;
  pageType: StrategyDetailType;
  dateRange?: string;
  hasChanges: boolean;
  // Settings
  initialCapital: number;
  backtestingPeriod: Period;
  numberOfStocks: number;
  portfolioConstruction: PortfolioConstructionType;
  leverage: number;
  rankingRule: RankingRuleType | undefined;
  resetStrategySettings: () => void;
}

export interface RecalculationStatus {
  type: "success" | "error" | null;
  message: string;
}

export interface UseStrategyRecalculateHandlerResult {
  handleRecalculate: () => Promise<void>;
  recalculationStatus: RecalculationStatus;
  setRecalculationStatus: (status: RecalculationStatus) => void;
  showSlotSelectionModal: boolean;
  setShowSlotSelectionModal: (show: boolean) => void;
  pendingCloneData: {
    modelId: string;
    universe: string;
    period: string;
  } | null;
  handleStrategySlotSelected: (overwrittenStrategy: Strategy) => Promise<void>;
  handleSlotSelectionModalClose: () => void;
  // API loading states
  recalculateStrategy: ReturnType<typeof useRecalculateStrategy>;
  updateStrategy: ReturnType<typeof useUpdateStrategy>;
  cloneStrategy: ReturnType<typeof useCloneStrategy>;
}

export const useStrategyRecalculateHandler = (
  options: UseStrategyRecalculateHandlerOptions
): UseStrategyRecalculateHandlerResult => {
  const {
    strategy,
    pageType,
    dateRange,
    hasChanges,
    initialCapital,
    backtestingPeriod,
    numberOfStocks,
    portfolioConstruction,
    leverage,
    rankingRule,
    resetStrategySettings,
  } = options;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setBusy } = useBusy();
  const { addInfoMessage } = useToast();
  const { getPeriodStart } = usePeriod();
  const { convertRankingRuleToAscending } = useRankingRule(strategy);
  const { universe } = useIndicatorKey();
  const queryClient = useQueryClient();

  // Hooks for API operations
  const recalculateStrategy = useRecalculateStrategy(strategy.modelId);
  const updateStrategy = useUpdateStrategy();
  const cloneStrategy = useCloneStrategy();

  // State management
  const [recalculationStatus, setRecalculationStatus] =
    useState<RecalculationStatus>({
      type: null,
      message: "",
    });
  const [showSlotSelectionModal, setShowSlotSelectionModal] = useState(false);
  const [pendingCloneData, setPendingCloneData] = useState<{
    modelId: string;
    universe: string;
    period: string;
  } | null>(null);

  // Common recalculation parameters builder
  const buildRecalculationParams = useCallback((): RecalculateStrategy => {
    return {
      startCash: initialCapital,
      period: backtestingPeriod as string,
      numberOfStocks: numberOfStocks,
      portfolioConstruction: portfolioConstruction,
      leverage: leverage,
      ascending: convertRankingRuleToAscending(
        portfolioConstruction,
        rankingRule
      ),
      backtestPeriod: getPeriodStart(backtestingPeriod),
      longsShare:
        portfolioConstruction === "ShortOnly"
          ? 0
          : portfolioConstruction === "LongShort"
          ? 0.5
          : 1,
      shortsShare:
        portfolioConstruction === "LongOnly"
          ? 0
          : portfolioConstruction === "LongShort"
          ? 0.5
          : 1,
    };
  }, [
    initialCapital,
    backtestingPeriod,
    numberOfStocks,
    portfolioConstruction,
    leverage,
    convertRankingRuleToAscending,
    rankingRule,
    getPeriodStart,
  ]);

  // Handle delivered strategy recalculation (clone-based)
  const handleDeliveredRecalculate = useCallback(async () => {
    if (!universe || !dateRange) {
      throw new Error(
        "Universe and dateRange are required for delivered strategy recalculation"
      );
    }

    setBusy(true);
    setRecalculationStatus({ type: null, message: "" });

    try {
      const recalculateParams = buildRecalculationParams();
      await cloneStrategy.mutateAsync({
        modelId: strategy.modelId,
        universe: universe,
        period: dateRange,
        newModelId: "draft",
        backtest: recalculateParams,
        overwriteExisting: true,
      });

      addInfoMessage(
        t(
          "settingsUI.recalculationDescription",
          "Your strategy recalculation has started. The process can take up to a minute. You will be notified once it's complete."
        ),
        t("settingsUI.recalculationStarted", "Recalculation in progress")
      );

      setRecalculationStatus({
        type: "success",
        message: t(
          "settingsUI.cloneAndRecalculationSuccess",
          "Strategy cloned and recalculated successfully!"
        ),
      });
      setBusy(false);
      navigate(`/draft`);
    } catch (error) {
      // Check if error is 403 with "No empty slot available" message
      if (
        error &&
        typeof error === "object" &&
        "status" in error &&
        "message" in error &&
        (error as { status: number; message: string }).status === 403 &&
        (error as { status: number; message: string }).message?.includes(
          "No empty slot available"
        )
      ) {
        setPendingCloneData({
          modelId: strategy.modelId,
          universe: universe,
          period: dateRange,
        });
        setShowSlotSelectionModal(true);
        setBusy(false);
        return;
      }

      throw error;
    }
  }, [
    universe,
    dateRange,
    setBusy,
    strategy.modelId,
    cloneStrategy,
    buildRecalculationParams,
    addInfoMessage,
    t,
    navigate,
  ]);

  const handleActivePortfolioRecalculate = useCallback(async () => {
    setBusy(true);
    setRecalculationStatus({ type: null, message: "" });

    const recalculateParams = buildRecalculationParams();
    console.log(
      "Recalculating active strategy with settings:",
      recalculateParams
    );

    await cloneStrategy.mutateAsync({
      modelId: strategy.modelId,
      newModelId: `${strategy.modelId}_draft`,
      backtest: recalculateParams,
      overwriteExisting: true,
    });

    addInfoMessage(
      t(
        "settingsUI.recalculationDescription",
        "Your strategy recalculation has started. The process can take up to a minute. You will be notified once it's complete."
      ),
      t("settingsUI.recalculationStarted", "Recalculation in progress")
    );

    setBusy(false);

    resetStrategyCacheData(`${strategy.modelId}_draft`, () => []);
    invalidateStrategyCacheData(strategy.modelId);
    if (strategy.modelId !== "draft") {
      navigate(`/${strategy.modelId.replace("_draft", "")}/draft`);
    }
    resetStrategySettings();

    setRecalculationStatus({
      type: "success",
      message: t(
        "settingsUI.recalculationSuccess",
        "Strategy recalculated successfully!"
      ),
    });
  }, [
    buildRecalculationParams,
    setBusy,
    strategy.modelId,
    addInfoMessage,
    t,
    navigate,
    cloneStrategy,
  ]);

  const resetStrategyCacheData = (
    id: string,
    defaultData: (oldData: Strategy[]) => Strategy[]
  ) => {
    queryClient.setQueryData(["model", id], defaultData);
    queryClient.setQueryData(["model", "chart", id], () => ({}));
    queryClient.setQueryData(["model", "table", id], () => ({}));
    queryClient.setQueryData(["model", "returns", id], () => ({}));
    queryClient.setQueryData(["model", "current-returns", id], () => ({}));
    queryClient.setQueryData(["model", "aggregated-returns", id], () => ({}));
    queryClient.setQueryData(["model", "drawdown", id], () => ({}));
  };

  const invalidateStrategyCacheData = (id: string) => {
    queryClient.refetchQueries({
      queryKey: ["model", id],
    });
  };

  const handleActiveRecalculate = useCallback(async () => {
    setBusy(true);
    setRecalculationStatus({ type: null, message: "" });

    const recalculateParams = buildRecalculationParams();
    console.log(
      "Recalculating active strategy with settings:",
      recalculateParams
    );

    await recalculateStrategy.mutateAsync(recalculateParams);

    addInfoMessage(
      t(
        "settingsUI.recalculationDescription",
        "Your strategy recalculation has started. The process can take up to a minute. You will be notified once it's complete."
      ),
      t("settingsUI.recalculationStarted", "Recalculation in progress")
    );

    setBusy(false);
    resetStrategyCacheData(strategy.modelId, (oldData: Strategy[]) => {
      if (!oldData || oldData.length === 0) {
        return [];
      } else {
        return [
          {
            ...oldData[0],
            backtest: {
              ...strategy.backtest,
              backtestInProgress: true,
            },
            CAGR: 0,
            Sharpe: 0,
            Volatility: 0,
          },
        ];
      }
    });
    if (strategy.modelId !== "draft") {
      navigate(`/${strategy.modelId.replace("_draft", "")}/draft`);
    }

    setRecalculationStatus({
      type: "success",
      message: t(
        "settingsUI.recalculationSuccess",
        "Strategy recalculated successfully!"
      ),
    });
  }, [
    buildRecalculationParams,
    recalculateStrategy,
    setBusy,
    strategy.modelId,
    addInfoMessage,
    t,
    navigate,
  ]);

  // Handle draft strategy recalculation (same as active for now)
  const handleDraftRecalculate = useCallback(async () => {
    // Draft strategies can be recalculated in place
    await handleActiveRecalculate();
  }, [handleActiveRecalculate]);

  // Handle draft-only strategy recalculation
  const handleDraftOnlyRecalculate = useCallback(async () => {
    // Draft-only strategies can be recalculated in place
    await handleActiveRecalculate();
  }, [handleActiveRecalculate]);

  // Main recalculation handler that delegates based on page type
  const handleRecalculate = useCallback(async () => {
    if (!hasChanges) return;

    try {
      switch (pageType) {
        case "delivered":
          await handleDeliveredRecalculate();
          break;
        case "active":
          await handleActivePortfolioRecalculate();
          break;
        case "draft":
          await handleDraftRecalculate();
          break;
        case "draft-only":
          await handleDraftOnlyRecalculate();
          break;
        default:
          throw new Error(`Unsupported page type: ${pageType}`);
      }
    } catch (error) {
      console.error(`Failed to recalculate ${pageType} strategy:`, error);

      // Hide busy indicator on error
      if (pageType === "delivered") {
        setBusy(false);
      }

      // Reset backtestInProgress to false if recalculation failed
      try {
        await updateStrategy.mutateAsync({
          modelId: strategy.modelId,
          backtest: {
            ...strategy.backtest,
            backtestInProgress: false,
          },
        });
      } catch (updateError) {
        console.error("Failed to reset backtestInProgress:", updateError);
      }

      setRecalculationStatus({
        type: "error",
        message:
          pageType === "delivered"
            ? t(
                "settingsUI.cloneAndRecalculationError",
                "Failed to clone and recalculate strategy. Please try again."
              )
            : t(
                "settingsUI.recalculationError",
                "Failed to recalculate strategy. Please try again."
              ),
      });
    }
  }, [
    hasChanges,
    pageType,
    handleDeliveredRecalculate,
    handleActiveRecalculate,
    handleDraftRecalculate,
    handleDraftOnlyRecalculate,
    setBusy,
    updateStrategy,
    strategy.modelId,
    strategy.backtest,
    t,
  ]);

  // Handle strategy slot selection for delivered strategies
  const handleStrategySlotSelected = useCallback(
    async (overwrittenStrategy: Strategy) => {
      if (!pendingCloneData) return;

      setShowSlotSelectionModal(false);
      setBusy(true);

      try {
        setRecalculationStatus({ type: null, message: "" });

        const cloneResponse = await cloneStrategy.mutateAsync(pendingCloneData);
        const targetModelId = cloneResponse.modelId;

        const recalculateParams = buildRecalculationParams();
        console.log(
          "Recalculating strategy after slot selection with settings:",
          recalculateParams
        );

        await updateStrategy.mutateAsync({
          modelId: targetModelId,
          backtest: {
            ...strategy.backtest,
            backtestInProgress: true,
          },
        });

        await recalculateStrategy.mutateAsync(recalculateParams);

        setRecalculationStatus({
          type: "success",
          message: t(
            "settingsUI.cloneAndRecalculationSuccess",
            `Strategy cloned and recalculated successfully! (Replaced ${overwrittenStrategy.name})`
          ),
        });

        setTimeout(() => {
          setBusy(false);
          navigate(`/${targetModelId}`);
        }, 1000);

        setPendingCloneData(null);
      } catch (error) {
        console.error(
          "Failed to clone and recalculate after slot selection:",
          error
        );
        setBusy(false);
        setPendingCloneData(null);

        setRecalculationStatus({
          type: "error",
          message: t(
            "settingsUI.cloneAndRecalculationError",
            "Failed to clone and recalculate strategy. Please try again."
          ),
        });
      }
    },
    [
      pendingCloneData,
      setBusy,
      cloneStrategy,
      buildRecalculationParams,
      updateStrategy,
      strategy.backtest,
      recalculateStrategy,
      t,
      navigate,
    ]
  );

  const handleSlotSelectionModalClose = useCallback(() => {
    setShowSlotSelectionModal(false);
    setPendingCloneData(null);
  }, []);

  return {
    handleRecalculate,
    recalculationStatus,
    setRecalculationStatus,
    showSlotSelectionModal,
    setShowSlotSelectionModal,
    pendingCloneData,
    handleStrategySlotSelected,
    handleSlotSelectionModalClose,
    // API loading states
    recalculateStrategy,
    updateStrategy,
    cloneStrategy,
  };
};
